'use client'

import { useEffect, useState, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { LDProvider } from 'launchdarkly-react-client-sdk'
import { getCookie, setCookie } from 'cookies-next'

export default function LaunchDarklyProvider({ children }: { children: React.ReactNode }) {
    const [anonymousID] = useState(() => uuidv4())
    const [patientId, setPatientId] = useState<string>('')

    // Function to check localStorage for patientId
    const updatePatientIdFromStorage = useCallback(() => {
        if (typeof window !== 'undefined') {
            const authedID = window.localStorage.getItem('patientId')
            const newPatientId = authedID || getCookie('launchdarkly_context_key')?.toString() || anonymousID
            setPatientId(newPatientId)
        }
    }, [anonymousID])

    // Initial check on mount
    useEffect(() => {
        updatePatientIdFromStorage()
    }, [updatePatientIdFromStorage])

    // Listen for storage events (when localStorage changes in another tab or via custom event)
    useEffect(() => {
        if (typeof window === 'undefined') return

        // Listen for storage changes from other tabs
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'patientId') {
                updatePatientIdFromStorage()
            }
        }

        // Listen for custom event dispatched when patientId is set in the same tab
        const handlePatientIdChange = () => {
            updatePatientIdFromStorage()
        }

        window.addEventListener('storage', handleStorageChange)
        window.addEventListener('patientIdChanged', handlePatientIdChange)

        return () => {
            window.removeEventListener('storage', handleStorageChange)
            window.removeEventListener('patientIdChanged', handlePatientIdChange)
        }
    }, [updatePatientIdFromStorage])

    useEffect(() => {
        if (patientId) {
            setCookie('launchdarkly_context_key', patientId, {
                sameSite: true,
                path: '/',
                domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
            })
        }
    }, [patientId])

    return (
        <LDProvider
            clientSideID={process.env.NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_SIDE_ID!}
            options={{
                useReport: true,
                baseUrl: 'https://app.launchdarkly.com',
                sendEventsOnlyForVariation: true,
            }}
            timeout={3}
            context={{
                key: patientId || anonymousID,
                kind: 'user',
            }}
        >
            {children}
        </LDProvider>
    )
}
